import { useState, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Grid, Stack, Paper, Button, Select, Checkbox, Text, Group, TextInput, Loader, Center, Badge, Modal, Alert } from '@mantine/core';
import { IconSparkles, IconCheck, IconX, IconChevronDown, IconChevronUp } from '@tabler/icons-react';
import MilkdownRenderer from './MilkdownRenderer';
import { ResumeContent } from '../../api/resumes';
import { useCompileArtifact } from '../../api/artifacts';
import { useGenerateArtifact, useGenerationStatus, useProviders, GenerationJob } from '../../api/llm';
import { useJobs } from '../../api/jobs';
import { usePromptTemplatesByType } from '../../api/promptTemplates';
import { useStylesheets } from '../../api/stylesheets';
import { useArtifactsStore } from '../../store/artifacts.store';
import { Job } from '../../api/jobs';
import { useCreateLlmRequest } from '../../api/llmRequests';

interface UnifiedArtifactCompilerProps {
  contentItems: ResumeContent[];
  onArtifactCreated: () => void;
  onGenerationStarted?: () => void;
  onPlaceholderCreated?: (placeholderId: string) => void;
  preSelectedJobId?: string;
  artifactType?: string;
  hideJobSelection?: boolean;
}

export default function UnifiedArtifactCompiler({
  contentItems,
  onArtifactCreated,
  onGenerationStarted,
  onPlaceholderCreated,
  preSelectedJobId,
  artifactType = 'resume',
  hideJobSelection = false,
}: UnifiedArtifactCompilerProps) {
  const { selectedMasterTemplateId, selectedContentIds, setSelectedTemplate, toggleContentSelection, clearSelection } =
    useArtifactsStore();

  const [selectedJobId, setSelectedJobId] = useState<string | null>(preSelectedJobId || null);
  const [artifactName, setArtifactName] = useState('');
  const [selectedPromptTemplateId, setSelectedPromptTemplateId] = useState<string | null>(null);
  const [selectedStylesheetId, setSelectedStylesheetId] = useState<string | null>(null);
  const [generatingJobId, setGeneratingJobId] = useState<string | null>(null);
  const [showGenerationModal, setShowGenerationModal] = useState(false);
  const [generatedContent, setGeneratedContent] = useState<string | null>(null);
  const [previewVisible, setPreviewVisible] = useState(false);
  const queryClient = useQueryClient();

  const compileMutation = useCompileArtifact();
  const generateMutation = useGenerateArtifact();
  const createLlmRequestMutation = useCreateLlmRequest();
  const { data: providers = [] } = useProviders();
  const { data: promptTemplates = [] } = usePromptTemplatesByType(artifactType as 'resume' | 'assessment' | 'jobParsing');
  const { data: stylesheets = [] } = useStylesheets();
  const { data: generationStatus } = useGenerationStatus(generatingJobId) as { data: GenerationJob | null | undefined };

  const { data: allJobs = [] } = useJobs();

  // Get default stylesheet
  const defaultStylesheet = useMemo(
    () => stylesheets.find((s) => s.is_default) || null,
    [stylesheets]
  );

  // Initialize stylesheet to default when it becomes available
  useMemo(() => {
    if (!selectedStylesheetId && defaultStylesheet) {
      setSelectedStylesheetId(defaultStylesheet.id);
    }
  }, [defaultStylesheet, selectedStylesheetId]);

  // Get master templates
  const masterTemplates = useMemo(() => {
    return contentItems.filter((item) => item.type === 'masterTemplate');
  }, [contentItems]);

  // Get default master template
  const defaultMasterTemplate = useMemo(
    () => {
      if (masterTemplates.length === 1) {
        // If only one template, it's considered default
        return masterTemplates[0];
      }
      // Otherwise find the one explicitly marked as default
      return masterTemplates.find(t => t.is_default) || null;
    },
    [masterTemplates]
  );

  // Auto-select default master template
  useMemo(() => {
    if (!selectedMasterTemplateId && masterTemplates.length > 0) {
      if (masterTemplates.length === 1) {
        // If only one template, auto-select it
        setSelectedTemplate(masterTemplates[0].id);
      } else {
        // Otherwise auto-select the default one
        const defaultTemplate = masterTemplates.find(t => t.is_default);
        if (defaultTemplate) {
          setSelectedTemplate(defaultTemplate.id);
        }
      }
    }
  }, [masterTemplates, selectedMasterTemplateId, setSelectedTemplate]);

  // Get non-template content
  const selectableContent = useMemo(() => {
    return contentItems.filter(
      (item) => item.type !== 'masterTemplate' && item.id !== selectedMasterTemplateId
    );
  }, [contentItems, selectedMasterTemplateId]);

  // Auto-check default content on first load (only run once)
  useMemo(() => {
    if (selectedContentIds.length === 0 && selectableContent.length > 0) {
      const defaultContent = selectableContent.filter(item => item.include_by_default);
      defaultContent.forEach(item => toggleContentSelection(item.id));
    }
  }, [selectableContent.length]); // Only depend on content length to trigger once

  // Auto-select default prompt template
  const defaultPromptTemplate = useMemo(
    () => promptTemplates.find(t => t.is_default) || null,
    [promptTemplates]
  );

  useMemo(() => {
    if (!selectedPromptTemplateId && defaultPromptTemplate) {
      setSelectedPromptTemplateId(defaultPromptTemplate.id);
    }
  }, [defaultPromptTemplate, selectedPromptTemplateId]);

  // Build preview
  const compiledPreview = useMemo(() => {
    if (!selectedMasterTemplateId) return '';

    const template = contentItems.find((item) => item.id === selectedMasterTemplateId);
    if (!template) return '';

    let markdown = template.content_markdown;

    selectedContentIds.forEach((contentId) => {
      const item = contentItems.find((i) => i.id === contentId);
      if (item) {
        markdown += `\n\n---\n\n${item.content_markdown}`;
      }
    });

    return markdown;
  }, [selectedMasterTemplateId, selectedContentIds, contentItems]);

  const handleGenerateWithAI = async () => {
    if (!selectedJobId) {
      console.error('Job must be selected for AI generation');
      return;
    }

    const selectedJob = allJobs.find((j) => j.id === selectedJobId);
    if (!selectedJob) {
      console.error('Selected job not found');
      return;
    }

    const primaryProvider = providers.find((p) => p.is_primary && p.is_active);
    if (!primaryProvider) {
      console.error('No primary LLM provider configured');
      return;
    }

    onGenerationStarted?.();

    try {
      // Auto-generate name if not provided
      let nameToUse = artifactName;
      if (!nameToUse) {
        nameToUse = `Resume for ${selectedJob.title} at ${selectedJob.company}`;
      }

      console.log('[Generation] Starting AI generation for job:', selectedJobId);
      console.log('[Generation] Using provider:', primaryProvider.name, primaryProvider.id);

      // Create a placeholder artifact to show in Job Resumes tab
      // This will be updated in-place by the backend when generation completes
      console.log('[Generation] Creating placeholder artifact...');
      const placeholderResult = await compileMutation.mutateAsync({
        job_id: selectedJobId,
        artifact_type: artifactType,
        name: nameToUse,
        content_markdown: '⏳ Generating with AI...\n\nPlease wait while we create your tailored resume. This may take a moment.',
        content_ids: selectedContentIds,
        stylesheet_id: selectedStylesheetId || undefined,
        metadata: {
          generation_status: 'processing',
          llm_provider: primaryProvider.name,
          started_at: new Date().toISOString(),
        },
      });

      const placeholderArtifactId = placeholderResult?.id;
      console.log('[Generation] Placeholder artifact created:', placeholderArtifactId);
      console.log('[Generation] Placeholder content:', placeholderResult?.content_markdown?.substring(0, 50));

      if (!placeholderArtifactId) {
        throw new Error('Failed to create placeholder artifact');
      }

      // Notify parent that placeholder was created so polling can track it
      if (onPlaceholderCreated) {
        onPlaceholderCreated(placeholderArtifactId);
      }

      // For Supabase backend, log the LLM request (Firebase handles this via the generation job)
      const backendType = import.meta.env.VITE_BACKEND_TYPE || 'supabase';
      if (backendType === 'supabase') {
        const requestBody = {
          job_id: selectedJobId,
          artifact_type: artifactType,
          content_ids: selectedContentIds,
          job_description: selectedJob.description_markdown || '',
          user_content: compiledPreview,
          job_title: selectedJob.title,
          company: selectedJob.company,
          prompt_template_id: selectedPromptTemplateId || undefined,
        };

        await createLlmRequestMutation.mutateAsync({
          job_id: selectedJobId,
          provider_name: primaryProvider.name,
          model_name: primaryProvider.model_id,
          artifact_type: artifactType,
          status: 'queued',
          request_body: requestBody,
        });
      }

      // Start the generation task, passing the placeholder artifact ID
      // so the backend can update it instead of creating a new one
      console.log('[Generation] Starting generation task with placeholder ID:', placeholderArtifactId);
      const result = await generateMutation.mutateAsync({
        job_id: selectedJobId,
        artifact_type: artifactType,
        content_ids: selectedContentIds,
        job_description: selectedJob.description_markdown || '',
        user_content: compiledPreview,
        job_title: selectedJob.title,
        company: selectedJob.company,
        provider_id: primaryProvider.id,
        prompt_template_id: selectedPromptTemplateId || undefined,
        artifact_id: placeholderArtifactId, // Pass placeholder ID so backend updates it
      });

      console.log('[Generation] Task created successfully:', result?.id);
      console.log('[Generation] Task status:', result?.status);

      // Close modal immediately - the actual generation happens in the background
      // The artifacts list will update automatically as generation completes
      console.log('[Generation] Closing modal');
      setShowGenerationModal(false);

      // Refresh artifacts cache to show the placeholder
      console.log('[Generation] Invalidating artifacts cache');
      queryClient.invalidateQueries({ queryKey: ['artifacts'] });

      // Call the onArtifactCreated callback to close the parent modal
      // This will close the create resume modal in JobListPage
      // Polling continues independently in JobListPage and stops when generation completes
      console.log('[Generation] Calling onArtifactCreated callback to close parent modal');
      onArtifactCreated();

    } catch (error) {
      console.error('Failed to start generation:', error);
      setShowGenerationModal(false);
    }
  };

  // Note: Generation completion is now handled via artifacts list updates
  // The modal closes immediately after the request is sent, and the artifacts
  // list refreshes automatically. No need for separate polling here.

  const selectedJobDetails = selectedJobId ? allJobs.find((j) => j.id === selectedJobId) : null;

  const handleReset = () => {
    setArtifactName('');
    clearSelection();
    setGeneratedContent(null);
    setGeneratingJobId(null);
    setPreviewVisible(false);
  };

  return (
    <Stack gap="lg">
      {/* 1. Job Posting - Full Width */}
      {preSelectedJobId && selectedJobDetails && (
        <Paper
          p="md"
          radius="md"
          withBorder
          style={{
            backgroundColor: 'var(--mantine-color-dark-6)',
          }}
        >
          <Group justify="space-between">
            <div>
              <Text fw={500} style={{ color: 'white' }}>
                {selectedJobDetails.title}
              </Text>
              <Text size="sm" c="dimmed">
                {selectedJobDetails.company}
              </Text>
            </div>
            <Badge>Locked</Badge>
          </Group>
        </Paper>
      )}

      {/* 2. Template & Stylesheet - Two Columns */}
      <Grid gutter="md">
        <Grid.Col span={{ base: 12, md: 6 }}>
          <div>
            <Text fw={500} mb="xs">
              Select Master Template
            </Text>
            <Select
              placeholder="Choose a template..."
              data={masterTemplates.map((item) => ({
                value: item.id,
                label: item.name,
              }))}
              value={selectedMasterTemplateId}
              onChange={(value) => setSelectedTemplate(value || undefined)}
              searchable
              description={defaultMasterTemplate ? `Default: ${defaultMasterTemplate.name}` : 'No default template set'}
            />
          </div>
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 6 }}>
          <div>
            <Text fw={500} mb="xs">
              Stylesheet (Optional)
            </Text>
            {stylesheets.length > 0 ? (
              <Select
                placeholder="Use default stylesheet"
                data={stylesheets.map((s) => ({
                  value: s.id,
                  label: s.name,
                }))}
                value={selectedStylesheetId}
                onChange={setSelectedStylesheetId}
                searchable
                clearable
                description={defaultStylesheet ? `Default: ${defaultStylesheet.name}` : 'No default stylesheet set'}
              />
            ) : (
              <Text size="sm" c="dimmed">
                No stylesheets available. Create one in Settings to use custom styling.
              </Text>
            )}
          </div>
        </Grid.Col>
      </Grid>

      {/* 3. Content Selection - Full Width as Table */}
      <div>
        <Text fw={500} mb="xs">
          Select Content to Include
        </Text>
        <Paper p="md" radius="md" withBorder style={{ overflowX: 'auto' }}>
          {selectableContent.length === 0 ? (
            <Text size="sm" c="dimmed">
              No content available. Create skills, experience, or other content first.
            </Text>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid var(--mantine-color-gray-6)' }}>
                  <th style={{ textAlign: 'left', padding: '12px', fontWeight: 500 }}>Include</th>
                  <th style={{ textAlign: 'left', padding: '12px', fontWeight: 500 }}>Content Title</th>
                  <th style={{ textAlign: 'left', padding: '12px', fontWeight: 500 }}>Content Type</th>
                  <th style={{ textAlign: 'left', padding: '12px', fontWeight: 500 }}>Default</th>
                </tr>
              </thead>
              <tbody>
                {selectableContent.map((item) => (
                  <tr key={item.id} style={{ borderBottom: '1px solid var(--mantine-color-gray-7)' }}>
                    <td style={{ padding: '12px' }}>
                      <Checkbox
                        checked={selectedContentIds.includes(item.id)}
                        onChange={() => toggleContentSelection(item.id)}
                      />
                    </td>
                    <td style={{ padding: '12px' }}>
                      <Text size="sm">{item.name}</Text>
                    </td>
                    <td style={{ padding: '12px' }}>
                      <Badge size="sm" variant="light">
                        {item.type}
                      </Badge>
                    </td>
                    <td style={{ padding: '12px' }}>
                      {item.include_by_default ? (
                        <Badge size="sm" color="green">
                          Yes
                        </Badge>
                      ) : (
                        <Text size="sm" c="dimmed">
                          —
                        </Text>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Paper>
      </div>

      {/* 4. Prompt Template & Name - Two Columns */}
      <Grid gutter="md">
        <Grid.Col span={{ base: 12, md: 6 }}>
          <div>
            <Text fw={500} mb="xs">
              Prompt Template
            </Text>
            {selectedJobId && promptTemplates.length > 0 ? (
              <Select
                placeholder="Use default template for this artifact type"
                clearable
                data={promptTemplates.map((template) => ({
                  value: template.id,
                  label: template.name,
                }))}
                value={selectedPromptTemplateId}
                onChange={setSelectedPromptTemplateId}
                description="Select a custom prompt template for AI generation"
              />
            ) : (
              <Text size="sm" c="dimmed">
                Job selection required to choose prompt template
              </Text>
            )}
          </div>
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 6 }}>
          <div>
            <Text fw={500} mb="xs">
              Name This Resume (Optional)
            </Text>
            <TextInput
              placeholder="Leave empty to auto-generate name"
              value={artifactName}
              onChange={(e) => setArtifactName(e.currentTarget.value)}
              disabled={compileMutation.isPending || generateMutation.isPending}
              description="Will be auto-generated as: Resume for [Job Title] at [Company]"
            />
          </div>
        </Grid.Col>
      </Grid>

      {/* 5. Buttons */}
      <Group justify="flex-end" gap="xs">
        <Button
          variant="light"
          onClick={handleReset}
          disabled={compileMutation.isPending || generateMutation.isPending}
        >
          Reset
        </Button>
        <Button
          onClick={handleGenerateWithAI}
          variant="filled"
          loading={generateMutation.isPending}
          leftSection={<IconSparkles size={16} />}
          disabled={!selectedMasterTemplateId || !selectedJobId || compileMutation.isPending}
        >
          Generate Resume with AI
        </Button>
      </Group>

      {/* 6. Preview - Hidden by Default */}
      <div>
        <Button
          variant="subtle"
          onClick={() => setPreviewVisible(!previewVisible)}
          rightSection={previewVisible ? <IconChevronUp size={16} /> : <IconChevronDown size={16} />}
          fullWidth
        >
          {previewVisible ? 'Hide Preview' : 'Show Preview'}
        </Button>

        {previewVisible && (
          <Paper p="md" radius="md" bg="gray.0" withBorder mt="md">
            {compileMutation.isPending ? (
              <Center style={{ height: '400px' }}>
                <Loader />
              </Center>
            ) : generatedContent || compiledPreview ? (
              <MilkdownRenderer content={generatedContent || compiledPreview} />
            ) : (
              <Text c="dimmed" size="sm">
                Select a template and content items to preview your resume
              </Text>
            )}
          </Paper>
        )}
      </div>

      {/* Generation Progress Modal */}
      <Modal
        opened={showGenerationModal}
        onClose={() => {
          if (!generateMutation.isPending && generatingJobId === null) {
            setShowGenerationModal(false);
          }
        }}
        title={`Generating Resume`}
        centered
        closeButtonProps={{ disabled: generateMutation.isPending }}
      >
        <Stack gap="md">
          {generationStatus?.status === 'pending' && (
            <>
              <Center>
                <Loader />
              </Center>
              <Text size="sm" c="dimmed" ta="center">
                Initializing AI generation...
              </Text>
            </>
          )}

          {generationStatus?.status === 'processing' && (
            <>
              <Center>
                <Loader />
              </Center>
              <Text size="sm" c="dimmed" ta="center">
                Generating your tailored resume...
              </Text>
            </>
          )}

          {generationStatus?.status === 'completed' && (
            <>
              <Alert icon={<IconCheck size={16} />} color="green">
                Generation completed successfully!
              </Alert>
              {generationStatus.token_usage && (
                <Text size="sm" c="dimmed">
                  Tokens used: {generationStatus.token_usage.total}
                </Text>
              )}
            </>
          )}

          {generationStatus?.status === 'failed' && (
            <>
              <Alert icon={<IconX size={16} />} color="red">
                {generationStatus.error || 'Generation failed. Please try again.'}
              </Alert>
            </>
          )}
        </Stack>
      </Modal>
    </Stack>
  );
}
